// Shared task filter engine — the field/operator model, condition evaluation,
// and the search + filter-group application pipeline. Extracted from TasksTable
// so every view (List, Board, Gallery, Timeline, Table) can filter through the
// same rules instead of only the table. Pure functions: state/UI live in
// hooks/useTaskFilters.js and components/tasks/TaskFilterBar.jsx.
import { format } from 'date-fns';
import { extractPlainText } from './constants';
import { statusName, statusValueName } from './statusName';

// Which operators each field type offers.
export const FIELD_OPERATORS = {
  text:         ['contains', 'not_contains', 'is_set', 'is_not_set'],
  url:          ['contains', 'not_contains', 'is_set', 'is_not_set'],
  number:       ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_set', 'is_not_set'],
  select:       ['eq', 'neq', 'is_set', 'is_not_set'],
  multi_select: ['contains', 'not_contains', 'is_set', 'is_not_set'],
  checkbox:     ['is_true', 'is_false'],
  date:         ['on', 'before', 'after', 'on_or_before', 'on_or_after', 'is_today', 'is_this_week', 'is_overdue', 'is_set', 'is_not_set'],
};

// Operator labels: math symbols as they are, the rest in the app's language.
const OPERATOR_SYMBOLS = { gt: '>', gte: '≥', lt: '<', lte: '≤' };
export const operatorLabel = (op, t) => OPERATOR_SYMBOLS[op] || t(`ui_views.filters.operators.${op}`);

export const NO_VALUE_OPERATORS = new Set(['is_set', 'is_not_set', 'is_true', 'is_false', 'is_today', 'is_this_week', 'is_overdue']);

// Dynamic "current user" token for assignee/owner filters. Resolved to the
// logged-in user's id at evaluation time, so saved filters and presets keep
// working per-user (Linear/ClickUp-style "Me" token).
export const ME_VALUE = '__me__';

// "No project" in filters and groups: a task without a project_id, or with one of a
// project the person cannot open (a trashed project's tasks keep its id until it is
// restored, routes/projects.delete_project). Keyed by that id, each trashed project made
// a "No Project" group of its own, and the "No Project" filter matched none of them.
export const NO_PROJECT = 'none';
export const shownProjectId = (task, projects = []) =>
  (task?.project_id && projects.some((p) => p.id === task.project_id) ? task.project_id : null);

// Local calendar day ("YYYY-MM-DD") for date filtering. due_date is already a
// plain date string; datetime fields (created_at) are converted to the local day.
export const toLocalDay = (v) => {
  if (!v) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && !v.includes('T')) return v.slice(0, 10);
  const d = new Date(v);
  return isNaN(d) ? null : format(d, 'yyyy-MM-dd');
};

// A filter condition only constrains results once it's "complete": no-value
// operators (is_set, is_true…) are always meaningful; value operators need a
// non-empty value. Opening the filter panel auto-adds a blank condition, which
// must NOT count as an active filter (button highlight) nor filter any tasks.
export const conditionIsActive = (c) =>
  !!c && (
    NO_VALUE_OPERATORS.has(c.operator) ||
    (c.value != null && c.value !== '' && !(Array.isArray(c.value) && c.value.length === 0))
  );

// Build the list of filterable fields, decoupled from table columns so any view
// can use it. ctx supplies the option lists + i18n. customFieldDefs adds the
// user's custom columns as filterable fields.
export function buildFilterableFields({
  t,
  customStatuses = [],
  statusConfig = {},
  priorityConfig = {},
  projects = [],
  labels = [],
  taskTypes = [],
  usersMap = {},
  teamsMap = {},
  customFieldDefs = [],
}) {
  const userOptions = [
    { value: ME_VALUE, label: t('tasks.me') },
    ...Object.entries(usersMap).map(([uid, u]) => ({ value: uid, label: u.full_name || uid })),
  ];

  const builtIn = [
    { key: 'title',      label: t('tasks.task_column'),  type: 'text' },
    { key: 'status',     label: t('tasks.status'),       type: 'select', options: customStatuses.length > 0
        ? customStatuses.filter(s => s.category !== 'archived').map(s => ({ value: s.id, label: statusName(s, t) }))
        : Object.keys(statusConfig).map(v => ({ value: v, label: statusValueName(v, t) })) },
    { key: 'priority',   label: t('tasks.priority'),     type: 'select', options: Object.entries(priorityConfig).map(([v, c]) => ({ value: v, label: c.altLabelKey ? t(c.altLabelKey) : c.label })) },
    { key: 'due_date',   label: t('tasks.due_date'),     type: 'date' },
    { key: 'duration',   label: t('tasks.duration'),     type: 'number' },
    { key: 'project',    label: t('tasks.project'),      type: 'select', options: [{ value: NO_PROJECT, label: t('tasks.no_project') }, ...projects.map(p => ({ value: p.id, label: p.name }))] },
    { key: 'labels',     label: t('tasks.labels'),       type: 'multi_select', options: labels.map(l => ({ value: l.id, label: l.name })) },
    { key: 'scheduled',  label: t('tasks.scheduled'),    type: 'checkbox' },
    { key: 'task_type',  label: t('tasks.type'),         type: 'select', options: taskTypes.map(tt => ({ value: tt.id, label: tt.name })) },
    // multi_select so "contains" matches ANY assignee, not just the first one
    { key: 'assignee',   label: t('tasks.assignee'),     type: 'multi_select', options: userOptions },
    { key: 'owner',      label: t('common.owner'),       type: 'select', options: userOptions },
    { key: 'team',       label: t('common.team'),        type: 'select', options: Object.values(teamsMap).map(tm => ({ value: tm.id, label: tm.name })) },
    { key: 'created_at', label: t('tasks.date_created'), type: 'date' },
  ];

  const custom = (customFieldDefs || []).map(def => ({
    key: `cf_${def.id}`,
    label: def.name,
    type: def.field_type,
    options: def.options?.map(o => ({ value: o.id, label: o.label, color: o.color })) || [],
  }));

  const extras = [
    { key: 'description', label: t('common.description'),        type: 'text' },
    { key: 'recurring',   label: t('tasks.filters.recurring'),   type: 'checkbox' },
  ];

  return [...builtIn, ...custom, ...extras];
}

// Evaluate a single filter condition against a task.
// ctx: { customStatuses, currentUserId, projects }
export function evaluateCondition(task, condition, ctx = {}) {
  const { customStatuses = [], currentUserId = null, projects = [] } = ctx;
  const { field, operator } = condition;
  // Resolve the dynamic "Me" token to the logged-in user's id
  const value = condition.value === ME_VALUE ? (currentUserId || condition.value) : condition.value;

  const getRawValue = () => {
    if (field === 'priority') return task.priority ?? null;
    if (field === 'status') {
      if (customStatuses.length > 0) {
        if (task.status_id) return task.status_id;
        const catMap = { todo: 'not_started', in_progress: 'in_progress', completed: 'completed' };
        return customStatuses.find(s => s.category === catMap[task.status])?.id ?? task.status;
      }
      return task.status;
    }
    if (field === 'title') return task.title ?? null;
    if (field === 'project') return shownProjectId(task, projects);
    if (field === 'due_date') return task.due_date || null;
    if (field === 'creator' || field === 'owner') return task.user_id ?? null;
    if (field === 'labels') return task.label_ids || [];
    if (field === 'task_type') return task.task_type_id ?? null;
    if (field === 'assignee') return task.assigned_to || [];
    if (field === 'description') return extractPlainText(task.description);
    if (field === 'recurring') return !!task.recurrence;
    if (field === 'team') return task.team_id || projects.find(p => p.id === task.project_id)?.team_id || null;
    if (field === 'scheduled') return !!task.scheduled_start;
    if (field === 'duration') return task.duration ?? null;
    if (field === 'created_at') return task.created_at ?? null;
    if (field.startsWith('cf_')) return task.custom_fields?.[field.slice(3)] ?? null;
    return null;
  };

  const raw = getRawValue();

  if (operator === 'is_set')     return raw != null && raw !== '' && !(Array.isArray(raw) && raw.length === 0);
  if (operator === 'is_not_set') return raw == null || raw === '' || (Array.isArray(raw) && raw.length === 0);
  if (operator === 'is_true')    return raw === true;
  if (operator === 'is_false')   return raw !== true;
  // A select's "none" option (No Project) is the empty value.
  const picked = field === 'project' && raw == null ? NO_PROJECT : raw;
  if (operator === 'eq')         return String(picked) === String(value);
  if (operator === 'neq')        return String(picked) !== String(value);
  if (operator === 'contains') {
    if (Array.isArray(raw)) return raw.includes(value);
    return String(raw ?? '').toLowerCase().includes(String(value).toLowerCase());
  }
  if (operator === 'not_contains') {
    if (Array.isArray(raw)) return !raw.includes(value);
    return !String(raw ?? '').toLowerCase().includes(String(value).toLowerCase());
  }
  if (operator === 'gt')     return Number(raw) > Number(value);
  if (operator === 'gte')    return Number(raw) >= Number(value);
  if (operator === 'lt')     return Number(raw) < Number(value);
  if (operator === 'lte')    return Number(raw) <= Number(value);
  if (operator === 'before') return !!raw && new Date(raw) < new Date(value);
  if (operator === 'after')  return !!raw && new Date(raw) > new Date(value);

  // Day-granular date operators (compare local calendar days)
  const day = toLocalDay(raw);
  if (operator === 'on')           return !!day && day === toLocalDay(value);
  if (operator === 'on_or_before') return !!day && day <= toLocalDay(value);
  if (operator === 'on_or_after')  return !!day && day >= toLocalDay(value);
  const today = format(new Date(), 'yyyy-MM-dd');
  if (operator === 'is_today')   return !!day && day === today;
  if (operator === 'is_overdue') return !!day && day < today;
  if (operator === 'is_this_week') {
    if (!day) return false;
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // ISO week start
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return day >= format(monday, 'yyyy-MM-dd') && day <= format(sunday, 'yyyy-MM-dd');
  }
  return true;
}

// Apply search + filter groups to a task list. (A OR B) AND (C OR D):
// each group is OR of its active conditions, groups are ANDed. Sorting,
// grouping, showCompleted and optimistic overrides stay in the caller.
export function applyTaskFilters(tasks, { searchQuery = '', filterGroups = [] } = {}, ctx = {}) {
  const query = (searchQuery || '').toLowerCase();
  return (tasks || []).filter(task => {
    if (query) {
      if (!(task.title || '').toLowerCase().includes(query) &&
          !(extractPlainText(task.description) || '').toLowerCase().includes(query)) {
        return false;
      }
    }
    if (filterGroups.length > 0) {
      const pass = filterGroups.every(group => {
        const active = group.conditions.filter(conditionIsActive);
        return active.length === 0 || active.some(cond => evaluateCondition(task, cond, ctx));
      });
      if (!pass) return false;
    }
    return true;
  });
}

// Quick-filter presets: one-click shortcuts that write regular filter
// conditions (single source of truth). t supplies localized labels.
export function buildFilterPresets(t) {
  return [
    { key: 'assigned_to_me', label: t('tasks.assigned_to_me'), groups: [[{ field: 'assignee', operator: 'contains', value: ME_VALUE }]] },
    { key: 'created_by_me',  label: t('tasks.created_by_me'),  groups: [[{ field: 'owner', operator: 'eq', value: ME_VALUE }]] },
    { key: 'my_tasks',       label: t('tasks.filters.my_tasks'),
      groups: [[{ field: 'assignee', operator: 'contains', value: ME_VALUE }, { field: 'owner', operator: 'eq', value: ME_VALUE }]] },
    { key: 'due_today',   label: t('tasks.filters.due_today'),  groups: [[{ field: 'due_date', operator: 'is_today', value: '' }]] },
    { key: 'overdue',     label: t('tasks.filters.overdue'),    groups: [[{ field: 'due_date', operator: 'is_overdue', value: '' }]] },
    { key: 'unassigned',  label: t('tasks.filters.unassigned'), groups: [[{ field: 'assignee', operator: 'is_not_set', value: '' }]] },
  ];
}

// Structural fingerprint of filter groups (ids stripped) so the menu can show
// which preset the current filters correspond to. Any manual tweak changes it.
export const presetFingerprint = (groups) =>
  JSON.stringify(groups.map(conds => conds.map(c => [c.field, c.operator, c.value])));

// Materialize a preset's plain [[...]] shape into runtime filter groups with ids.
export const presetToFilterGroups = (preset) =>
  preset.groups.map(conditions => ({
    id: crypto.randomUUID(),
    conditions: conditions.map(c => ({ id: crypto.randomUUID(), ...c })),
  }));

export const hasActiveFilters = (searchQuery, filterGroups) =>
  !!searchQuery || (filterGroups || []).some(g => g.conditions.some(conditionIsActive));
